import { OrderedSet } from 'immutable';
//import { PDFViewer } from 'pdfjs-dist/lib/pdf';

import {
  EventBus,
  PDFFindController,
  PDFLinkService,
} from 'pdfjs-dist/web/pdf_viewer';
//import { PDFFindController } from 'pdfjs-dist/web/pdf_viewer/types/web/pdf_find_controller';
import React from 'react';
import { usePDFFindController } from '../hooks/useFindController';

import { ErrorType, PDFData, ReadStatEvent, TOCItem } from '@readcloud/data';
import {
  applicationSelectors,
  readerActions,
  reportActions,
  useAppDispatch,
} from '@readcloud/state';
import { usePDFDocument } from '../hooks/usePDFDocument';
import { Rendition } from '../types/epubs';
import { FindController } from '../types/FindController';
import { makePageLabels } from '../utils/utils';
import { DocumentWithExtrasContext, PDFTreeNode } from './DocumentWithExtras';
import { useSelector } from 'react-redux';

/**
 * Use this to turn getPDFDocument into react state and use it in your componentry.
 *
 * You will also get a bunch of "extras" like a linkService, findController, page labels, and an outline (TOC)
 */
export const DocumentWithExtrasProvider: React.FC<{ children?: React.ReactNode }> = (props) => {
  const appConfig = useSelector(applicationSelectors.getAppConfig);

  //=============
  // set by the prepareDocument hook in the wrapper app.
  const [data, setData] = React.useState<PDFData | ArrayBuffer>();
  const [type, setType] = React.useState<string | undefined>();
  const [progress, setProgress] = React.useState<number>();
  const [preparingDocumentData, setPreparingDocumentData] =
    React.useState<boolean>();
  const [prepareError, setPrepareError] = React.useState<ErrorType>();
  //=============

  // this tracks the pageNumber as a string, useful for UI. readerData.viewstate saves the current viewstate,
  // including epub CFI's, which should be used for passing the current page into relevant functions.
  const [location, setLocation] = React.useState<string>();

  // ============
  // these values are responsible for zooming and remaining in the same location while zooming.
  const [scale, setScale] = React.useState<number>(1);
  const [temporaryScale, setTemporaryScale] = React.useState<number>(1);
  const [elementOrigin, setElementOrigin] = React.useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [screenOrigin, setScreenOrigin] = React.useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  // ============

  const {
    data: pdfDocument,
    isLoading,
    error,
  } = usePDFDocument({
    prepareError,
    type,
    data,
  });

  //Timeout refs for debounced actions.
  const updatePageNumberTimeoutRef = React.useRef<NodeJS.Timeout>();
  const updateReadStatTimeoutRef = React.useRef<NodeJS.Timeout>();
  const updateScaleTimeoutRef = React.useRef<NodeJS.Timeout>();

  //store the current readstat data in a useRef so the useEffect always has access to it. (maybe I should be doing this another way)
  const readStatData = React.useRef<ReadStatEvent>(null);

  const [pdfFindController, setPdfFindController] =
    React.useState<PDFFindController>();

  const [pdfLinkService, setPdfLinkService] = React.useState<PDFLinkService>();
  const [outline, setOutline] = React.useState<TOCItem[]>();
  const [labels, setLabels] = React.useState<
    { value: string; label: string }[]
  >([]);

  const [findController, setFindController] = React.useState<FindController>();
  const dispatch = useAppDispatch();
  /* Destroy pdf document whenever it is switched, or hook is unmounted */
  React.useEffect(() => {
    return () => {
      if (pdfDocument) {
        pdfDocument.destroy();
      }
    };
  }, [pdfDocument]);

  const [extras, setExtras] = React.useState<
    | {
        pdfFindController?: PDFFindController;
        linkService?: PDFLinkService;
        outline: TOCItem[];
        labels: { value: string; label: string }[];
      }
    | undefined
  >();

  React.useEffect(() => {
    let cancelled = false;
    // setup for a PDF document, EPub setup is done in EpubJsReader.ts
    if (pdfDocument && type === 'PDF') {
      //alert('loading' + isLoading);

      const pdfViewer = {
        pagesRotation: 0,
        pagesCount: pdfDocument.numPages,
        getPageTextContent: async (pageNumber: number) =>
          await pdfDocument.getPage(pageNumber).then((page: any) =>
            page.getTextContent({
              normalizeWhitespace: true,
            })
          ),

        set currentPageNumber(pageNumber: number) {
          dispatch(
            readerActions.updateDocumentControl({
              payload: { location: pageNumber.toString() },
              type: 'changeLocation',
            })
          );
        },
        get currentPageNumber() {
          return location ? parseInt(location) : -1;
        },
        isPageVisible: (pageNumber: number) => true,
        scrollPageIntoView: ({
          pageNumber,
          destArray,
        }: {
          pageNumber: number;
          destArray: any[];
        }) => {
          dispatch(
            readerActions.updateDocumentControl({
              payload: { location: pageNumber.toString() },
              type: 'changeLocation',
            })
          );
        },
      };

      // rcLogger.debug('PDF Viewer', pdfViewer, 'IsLoading?', isLoading);

      const eventBus = new EventBus();

      const pdfLinkService = new PDFLinkService();
      pdfLinkService.setViewer(pdfViewer);
      pdfLinkService.setDocument(pdfDocument);

      const pdfFindController = new PDFFindController({
        linkService: pdfLinkService,
        eventBus,
      });

      pdfFindController.setDocument(pdfDocument);

      setPdfLinkService(pdfLinkService);

      const promises = Promise.all([
        makePageLabels(pdfDocument).catch((e) => null),
        new Promise<PDFTreeNode[] | undefined>((resolve) =>
          pdfDocument.getOutline().then((outline) => resolve(outline))
        ),
      ]);

      setPdfFindController(pdfFindController);

      promises.then(([labels, outline]) => {
        if (!cancelled && labels) {
          setLabels(labels);
        }
        if (!cancelled && outline) {
          setOutline(outline);
        }
      });

      return () => {
        cancelled = true;
      };
    } else {
      return () => {
        cancelled = true;
      };
    }
  }, [pdfDocument, type]);

  const { controller } = usePDFFindController(pdfFindController);

  React.useEffect(() => {
    if (controller) {
      setFindController(controller);
    }
  }, [controller]);

  React.useEffect(() => {
    setExtras((s) => ({
      pdfFindController,
      linkService: pdfLinkService,
      outline: outline || [],
      labels,
    }));
  }, [outline, labels, pdfFindController, pdfLinkService]);

  //just pass location for PDF's,
  //location references the "start location" for epubs.
  const setPageLocation = (
    location: string,
    rendition?: Rendition,
    endLocation?: string
  ) => {
    //if ReadStat Data exists, that means we set it on the previous setPageLocation call.
    //So we can send that and use the current time as the time closed.
    const createReadStat = () => {
      readStatData.current &&
        dispatch(
          reportActions.asyncActions.createReadStat({
            ...readStatData.current,
            timeClosed: new Date(),
          })
        ).then(() => {
          //we reset it to undefined here to make sure it only dispatches once.
          readStatData.current = undefined;
        });
    };

    const debouncedLocationUpdate = () => {
      clearTimeout(updatePageNumberTimeoutRef.current);
      updatePageNumberTimeoutRef.current = setTimeout(() => {
        type &&
          (type === 'PDF'
            ? dispatch(
                readerActions.asyncActions.updateViewState({
                  location: !isNaN(parseInt(location)) ? location : '1',
                  pageNumberStart: parseInt(location),
                  pageNumberEnd: parseInt(location),
                })
              )
            : dispatch(
                readerActions.asyncActions.updateViewState({
                  location: location,
                  pageNumberStart: parseInt(
                    rendition?.book.locations
                      .locationFromCfi(location)
                      .toString()
                  ),
                  pageNumberEnd: parseInt(
                    rendition?.book.locations
                      .locationFromCfi(endLocation)
                      .toString()
                  ),
                })
              ));
      }, 300);
    };

    //after x amount of seconds, we set ReadStatData with the current page.
    const debouncedReadStatUpdate = () => {
      clearTimeout(updateReadStatTimeoutRef.current);
      updateReadStatTimeoutRef.current = setTimeout(
        () => {
          type &&
            (type === 'PDF'
              ? (readStatData.current = {
                  type: 'pageOpened',
                  pageStart: parseInt(location),
                  pageEnd: parseInt(location),
                  timeOpened: new Date(),
                })
              : (readStatData.current = {
                  type: 'pageOpened',
                  pageStart: parseInt(
                    rendition?.book.locations
                      .locationFromCfi(location)
                      .toString()
                  ),
                  pageEnd: parseInt(
                    rendition?.book.locations
                      .locationFromCfi(endLocation)
                      .toString()
                  ),
                  timeOpened: new Date(),
                }));
        },
        appConfig?.readStat?.minTimeOnPageSec
          ? appConfig?.readStat?.minTimeOnPageSec * 1000
          : 5000
      );
    };

    //call the 3 functions here on every change.
    createReadStat();
    debouncedLocationUpdate();
    debouncedReadStatUpdate();

    if (rendition) {
      setLocation(
        rendition.book.locations.locationFromCfi(location).toString()
      );
    } else {
      setLocation(location);
    }
  };

  React.useEffect(() => {
    const debouncedUpdate = () => {
      clearTimeout(updateScaleTimeoutRef.current);
      updateScaleTimeoutRef.current = setTimeout(() => {
        if (type && type === 'PDF') {
          dispatch(
            readerActions.asyncActions.updateViewState({
              scale: temporaryScale,
            })
          );
          setScale(temporaryScale);
        }
      }, 300);
    };
    debouncedUpdate();
  }, [temporaryScale]);

  const setScaleInstantly = (
    scale: number | ((prevState: number) => number)
  ) => {
    setScale(scale);
    setTemporaryScale(scale);
  };

  return (
    <DocumentWithExtrasContext.Provider
      value={{
        setData,
        setLocation: setPageLocation,
        setScale: setScaleInstantly,
        setElementOrigin,
        setScreenOrigin,
        setTemporaryScale,
        setType,
        setProgress,
        setPrepareError,

        setPreparingDocumentData,
        setFindController,
        setOutline,
        prepareError,
        loading: isLoading || preparingDocumentData,
        pdfDocument: pdfDocument,
        error: error,
        type,
        data,
        progress,
        extras,
        findController,
        location,
        scale,
        elementOrigin,
        screenOrigin,
        temporaryScale,
      }}
    >
      {props.children}
    </DocumentWithExtrasContext.Provider>
  );
};
